import React, { useEffect, useState, useRef } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { CheckCircle, Download, ArrowRight } from "@phosphor-icons/react";
import { Spinner } from "../components/ui-bits";
import { Wordmark } from "../components/Logo";
import { api, API } from "../lib/api";

export default function PaymentReturn() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const sessionId = params.get("session_id");
  const [status, setStatus] = useState("verifying"); // verifying | processing | done | error
  const [result, setResult] = useState(null);
  const [msg, setMsg] = useState("Verifying your payment…");
  const started = useRef(false);

  useEffect(() => {
    if (started.current) return;
    started.current = true;
    const isBulk = params.get("bulk") === "1";
    const pending = JSON.parse(localStorage.getItem(isBulk ? "vcoin_pending_bulk" : "vcoin_pending_dpp") || "null");
    if (!sessionId || !pending) { setStatus("error"); setMsg("Missing payment context."); return; }

    let attempts = 0;
    const poll = async () => {
      attempts += 1;
      try {
        const { data } = await api.get(`/payments/status/${sessionId}`);
        if (data.payment_status === "paid") {
          setStatus("processing");
          setMsg(isBulk ? "Payment confirmed. Issuing your passports…" : "Payment confirmed. Anchoring passport to the ledger…");
          if (isBulk) {
            const gen = await api.post("/dpp/generate-bulk", { session_id: sessionId, merchant_id: pending.merchant_id });
            localStorage.removeItem("vcoin_pending_bulk");
            setResult({ bulk: true, issued: gen.data.issued, count: gen.data.count });
          } else {
            const gen = await api.post("/dpp/generate", pending);
            localStorage.removeItem("vcoin_pending_dpp");
            setResult(gen.data);
          }
          setStatus("done");
          return;
        }
        if (attempts > 30) { setStatus("error"); setMsg("Payment not completed."); return; }
        setTimeout(poll, 2000);
      } catch (e) {
        if (attempts > 30) { setStatus("error"); setMsg(e?.response?.data?.detail || "Something went wrong."); return; }
        setTimeout(poll, 2000);
      }
    };
    poll();
    // eslint-disable-next-line
  }, []);

  return (
    <div className="min-h-screen bg-navy flex items-center justify-center px-6">
      <div className="w-full max-w-md bg-white rounded-3xl shadow-luxe-lg p-9 text-center animate-fade-up">
        <div className="flex justify-center mb-6"><Wordmark /></div>

        {status !== "done" && status !== "error" && (
          <div className="py-8">
            <div className="flex justify-center mb-6"><Spinner className="h-12 w-12" /></div>
            <p className="text-navy/70 text-sm">{msg}</p>
          </div>
        )}

        {status === "error" && (
          <div className="py-8">
            <p className="text-score-red text-sm">{msg}</p>
            <button onClick={() => navigate("/products")} className="mt-6 text-sm text-navy underline">Back to Products</button>
          </div>
        )}

        {status === "done" && result && result.bulk && (
          <div data-testid="bulk-success">
            <div className="flex justify-center mb-4"><CheckCircle size={56} weight="fill" className="text-score-green" /></div>
            <h1 className="text-2xl text-navy">{result.count} Passports Issued</h1>
            <p className="text-navy/50 text-sm mt-1">Permanent Digital Product Passports are now live.</p>
            <div className="mt-6 bg-cream rounded-2xl p-5 text-left max-h-52 overflow-y-auto">
              {result.issued.map((it) => (
                <div key={it.certificateId} className="flex items-center justify-between py-2 border-b border-navy/5 last:border-0">
                  <span className="text-sm text-navy truncate mr-3">{it.productName}</span>
                  <span className="text-[0.68rem] font-mono text-navy/40">{it.certificateId}</span>
                </div>
              ))}
            </div>
            <button onClick={() => navigate("/passports")} className="mt-5 w-full flex items-center justify-center gap-2 bg-navy text-cream rounded-xl py-3.5 text-sm hover:brightness-110 transition-all">
              View all passports <ArrowRight size={15} weight="bold" />
            </button>
          </div>
        )}

        {status === "done" && result && !result.bulk && (
          <div>
            <div className="flex justify-center mb-4"><CheckCircle size={56} weight="fill" className="text-score-green" /></div>
            <h1 className="text-2xl text-navy">Passport Issued</h1>
            <p className="text-navy/50 text-sm mt-1">A permanent Digital Product Passport is now live.</p>

            <div className="mt-6 bg-cream rounded-2xl p-5">
              <img src={result.qrCode} alt="QR code" className="mx-auto h-44 w-44" data-testid="success-qr" />
              <p className="mt-3 text-[0.62rem] uppercase tracking-[0.2em] text-navy/40">Certificate ID</p>
              <p className="text-navy font-medium text-sm" data-testid="success-certificate-id">{result.certificateId}</p>
            </div>

            <a
              href={`${API}/qr/${result.certificateId}?format=png`}
              data-testid="download-qr-button"
              className="mt-5 w-full flex items-center justify-center gap-2 bg-navy text-cream rounded-xl py-3.5 text-sm hover:brightness-110 transition-all"
            >
              <Download size={17} weight="light" /> Download QR Code
            </a>
            <button onClick={() => navigate("/passports")} className="mt-2 w-full flex items-center justify-center gap-2 text-navy/60 text-sm py-2.5 hover:text-gold transition-colors">
              View all passports <ArrowRight size={15} weight="bold" />
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
